import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { ApiService } from '../../../core/services/api.service';
import { API_ROUTES } from '../../../core/constants/api-routes';
import {
  FamilyMemberItem,
  FamilyMembersData,
  FamilyMembersRequest,
  FamilySpaceCountData,
  FamilyTreeGroup,
  LovedOnesFamilySpace,
  LovedOnesPageData,
  LovedOnesSummaryCount,
} from '../../../core/models/loved-ones.models';

import { AddFamilyMember } from './add-family-member/add-family-member';
import { CreateFamily } from './create-family/create-family';
import { DeleteConfirmation } from '../../../shared/components/delete-confirmation/delete-confirmation';

@Component({
  selector: 'app-loved-ones',
  imports: [RouterLink, AddFamilyMember, CreateFamily, DeleteConfirmation],
  templateUrl: './loved-ones.html',
  styleUrl: './loved-ones.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LovedOnes implements OnInit {

  // ── Dependency injection ─────────────────────────────────────────────────
  private readonly api = inject(ApiService);

  // ── Modal state ────────────────────────────────────────────────────────────
  readonly showAddMemberModal = signal(false);
  readonly showCreateFamilyModal = signal(false);

  /** Member being edited — null for add mode. */
  readonly editingMember = signal<FamilyMemberItem | null>(null);
  /** Family being edited — null for create mode. */
  readonly editingFamily = signal<LovedOnesFamilySpace | null>(null);

  /** Tracks which member's dropdown menu is open. */
  readonly openMenuId = signal<string | null>(null);

  /** Member pending removal confirmation. */
  readonly memberToRemove = signal<FamilyMemberItem | null>(null);

  // ── Page-level UI state ──────────────────────────────────────────────────

  /** True until the initial page-data API call completes. */
  readonly isInitializing = signal(true);
  /** True when the page-data API call failed. */
  readonly hasError = signal(false);

  /** Role of the logged-in user in the active family space (e.g. 'Owner', 'Partner'). */
  readonly currentUserRole = signal<string>('');

  /**
   * True when the logged-in user is the owner of the currently selected family space.
   * Checks the `isOwner` flag returned by the API (if present) and falls back to
   * comparing the role string (case-insensitive) so both API shapes are supported.
   */
  readonly isActiveSpaceOwner = computed<boolean>(() => {
    const id = this.activeFamilySpaceId();
    const space = this.familySpaceList().find(f => f.id === id);
    if (space?.isOwner !== undefined) return !!space.isOwner;
    return this.currentUserRole().toLowerCase() === 'owner';
  });

  /** Controls tree vs grid view. */
  readonly viewMode = signal<'tree' | 'grid'>('tree');

  // ── Page data (overall / cross-family) ───────────────────────────────────

  /** Overall summary counts (across all families). */
  readonly summaryCount = signal<LovedOnesSummaryCount>({
    familyMembersCount:           0,
    totalStoryCount:              0,
    averageStoriesPerPersonCount: 0,
  });

  /** Family space list for the dropdown. */
  readonly familySpaceList = signal<LovedOnesFamilySpace[]>([]);

  /** ID of the currently selected family space. */
  readonly activeFamilySpaceId = signal<string | null>(null);

  // ── Members data (per selected family space) ─────────────────────────────

  /** True while the family-members API call is in flight. */
  readonly isMembersLoading = signal(false);
  /** True when the family-members API call failed. */
  readonly hasMembersError = signal(false);

  /** Flat list of members returned by the members API. */
  readonly familyMembers = signal<FamilyMemberItem[]>([]);

  /** IDs of members whose profile photo failed to load (tree view). */
  private readonly _failedTreePhotoIds = signal(new Set<string>());
  /** IDs of members whose profile photo failed to load (grid view). */
  private readonly _failedGridPhotoIds = signal(new Set<string>());

  /** Per-space counts (members + stories) from the members API response. */
  readonly familySpaceCountData = signal<FamilySpaceCountData>({
    familyMembersCount: 0,
    totalStoryCount:    0,
  });

  // ── Grid pagination ──────────────────────────────────────────────────────

  private readonly gridPageSize = 9;
  readonly gridPage = signal(1);
  readonly gridTotalRecords = signal(0);
  readonly isLoadingMore = signal(false);
  readonly hasMoreRecords = computed(
    () => this.familyMembers().length < this.gridTotalRecords(),
  );

  /**
   * Derived: members grouped by familyTreeLevelId, sorted ascending.
   * Used exclusively by the tree view.
   */
  readonly treeGroups = computed<FamilyTreeGroup[]>(() => {
    const members = this.familyMembers();
    const map = new Map<number, FamilyTreeGroup>();

    for (const m of members) {
      const id = m.familyTreeLevelId;
      if (!map.has(id)) {
        map.set(id, {
          levelId:        id,
          levelName:      m.familyTreeLevel,
          hasCurrentUser: false,
          members:        [],
        });
      }
      const group = map.get(id)!;
      group.members.push(m);
      if (m.isLoggedInUser) group.hasCurrentUser = true;
    }

    return Array.from(map.values()).sort((a, b) => a.levelId - b.levelId);
  });

  // ────────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ────────────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadPageData();
  }

  // ────────────────────────────────────────────────────────────────────────
  // API — page data (family space list + overall summary)
  // ────────────────────────────────────────────────────────────────────────

  private async loadPageData(): Promise<void> {
    this.hasError.set(false);

    try {
      const resp = await this.api.request<LovedOnesPageData>(
        'GET',
        API_ROUTES.GET_LOVED_ONES_PAGE_DATA,
        undefined,
        { showToaster: false, showLoader: false, useToken: true },
      );

      if (resp.status && resp.data) {
        const d = resp.data;
        this.summaryCount.set(d.summaryCount ?? {
          familyMembersCount:           0,
          totalStoryCount:              0,
          averageStoriesPerPersonCount: 0,
        });
        const list = d.familySpaceList ?? [];
        this.familySpaceList.set(list);
        if (list.length > 0) {
          const currentId = this.activeFamilySpaceId();
          const targetId = (currentId && list.some(s => s.id === currentId))
            ? currentId
            : list[0].id;
          this.activeFamilySpaceId.set(targetId);
          // Only load members here on initial page load (no active space yet).
          // When called after add/edit, the caller loads members directly to avoid
          // a duplicate API call to GetFamilyMembersByFamilySpaceId.
          if (!currentId) {
            await this.loadFamilyMembers(targetId);
          }
        }
      } else {
        this.hasError.set(true);
      }
    } catch {
      this.hasError.set(true);
    } finally {
      this.isInitializing.set(false);
    }
  }

  // ────────────────────────────────────────────────────────────────────────
  // API — family members for the selected family space
  // ────────────────────────────────────────────────────────────────────────

  async loadFamilyMembers(familySpaceId: string, append = false): Promise<void> {
    if (!familySpaceId) return;

    if (append) {
      this.isLoadingMore.set(true);
    } else {
      this.isMembersLoading.set(true);
      this.gridPage.set(1);
      this.gridTotalRecords.set(0);
    }
    this.hasMembersError.set(false);

    const isGrid = this.viewMode() === 'grid';
    const body: FamilyMembersRequest = {
      familySpaceId,
      type: isGrid ? 2 : 1,
      pageNumber: isGrid ? this.gridPage() : 0,
      pageSize: isGrid ? this.gridPageSize : 0,
    };

    try {
      const resp = await this.api.request<FamilyMembersData>(
        'POST',
        API_ROUTES.GET_FAMILY_MEMBERS_BY_SPACE,
        body,
        { showToaster: false, showLoader: false, useToken: true },
      );

      if (resp.status && resp.data) {
        this.familySpaceCountData.set(resp.data.familySpaceCountData ?? {
          familyMembersCount: 0,
          totalStoryCount:    0,
        });
        this.gridTotalRecords.set(resp.data.totalRecords ?? 0);
        const newMembers = resp.data.familyMembers ?? [];
        if (append) {
          this.familyMembers.update(existing => [...existing, ...newMembers]);
        } else {
          this.familyMembers.set(newMembers);
          // Clear stale failed-photo tracking whenever the member list is replaced.
          this._failedTreePhotoIds.set(new Set());
          this._failedGridPhotoIds.set(new Set());
          // Reset role when switching family space; re-derive below.
          this.currentUserRole.set('');
        }
        // Update role whenever the logged-in user appears in the batch.
        const me = newMembers.find(m => m.isLoggedInUser);
        if (me?.relationship) {
          this.currentUserRole.set(me.relationship);
        }
      } else {
        if (!append) this.familyMembers.set([]);
        this.hasMembersError.set(true);
      }
    } catch {
      if (!append) this.familyMembers.set([]);
      this.hasMembersError.set(true);
    } finally {
      this.isMembersLoading.set(false);
      this.isLoadingMore.set(false);
    }
  }

  /** Loads the next page of grid members and appends to the list. */
  loadMore(): void {
    const id = this.activeFamilySpaceId();
    if (!id || this.isLoadingMore()) return;
    this.gridPage.update(p => p + 1);
    this.loadFamilyMembers(id, true);
  }

  // ────────────────────────────────────────────────────────────────────────
  // User interaction handlers
  // ────────────────────────────────────────────────────────────────────────

  /** Switches between tree and grid view and reloads members. */
  setViewMode(mode: 'tree' | 'grid'): void {
    this.viewMode.set(mode);
    const id = this.activeFamilySpaceId();
    if (id) {
      this.loadFamilyMembers(id);
    }
  }

  /** Handles family space dropdown change — reloads member list. */
  onFamilySpaceChange(id: string): void {
    if (!id) return;
    this.activeFamilySpaceId.set(id);
    this.loadFamilyMembers(id);
  }

  // ────────────────────────────────────────────────────────────────────────
  // Template utility helpers
  // ────────────────────────────────────────────────────────────────────────

  /** Returns the name of the currently selected family space. */
  getActiveFamilyName(): string {
    const id = this.activeFamilySpaceId();
    if (!id) return '';
    return this.familySpaceList().find(f => f.id === id)?.familyName ?? '';
  }

  // ── Add / Edit Member modal ──────────────────────────────────────────────

  openAddMemberModal(): void {
    // Toggle: clicking again while the add panel is open collapses it
    if (this.showAddMemberModal() && !this.editingMember()) {
      this.closeAddMemberModal();
      return;
    }
    // Close the other inline panel if open
    if (this.showCreateFamilyModal() && !this.editingFamily()) {
      this.closeCreateFamilyModal();
    }
    this.editingMember.set(null);
    this.showAddMemberModal.set(true);
    this.scrollToPanel('app-add-family-member');
  }

  openEditMemberModal(member: FamilyMemberItem): void {
    // Close the create-family inline panel if open
    if (this.showCreateFamilyModal()) {
      this.closeCreateFamilyModal();
    }
    this.editingMember.set(member);
    this.showAddMemberModal.set(true);
    this.openMenuId.set(null);
    this.scrollToPanel('app-add-family-member');
  }

  closeAddMemberModal(): void {
    this.showAddMemberModal.set(false);
    this.editingMember.set(null);
  }

  onMemberAdded(): void {
    this.showAddMemberModal.set(false);
    this.editingMember.set(null);
    const id = this.activeFamilySpaceId();
    if (id) this.loadFamilyMembers(id);
    this.loadPageData();
  }

  // ── Create / Edit Family modal ───────────────────────────────────────────

  openCreateFamilyModal(): void {
    // Toggle: clicking again while the create panel is open collapses it
    if (this.showCreateFamilyModal() && !this.editingFamily()) {
      this.closeCreateFamilyModal();
      return;
    }
    // Close the add member panel if open (add or edit mode)
    if (this.showAddMemberModal()) {
      this.closeAddMemberModal();
    }
    this.editingFamily.set(null);
    this.showCreateFamilyModal.set(true);
    this.scrollToPanel('app-create-family');
  }

  openEditFamilyModal(): void {
    const id = this.activeFamilySpaceId();
    if (!id) return;
    // Close the add-member inline panel if open
    if (this.showAddMemberModal()) {
      this.closeAddMemberModal();
    }
    // Toggle: clicking Edit again while the edit panel is already open collapses it
    if (this.showCreateFamilyModal() && this.editingFamily()) {
      this.closeCreateFamilyModal();
      return;
    }
    const family = this.familySpaceList().find(f => f.id === id) ?? null;
    this.editingFamily.set(family);
    this.showCreateFamilyModal.set(true);
    this.scrollToPanel('app-create-family');
  }

  closeCreateFamilyModal(): void {
    this.showCreateFamilyModal.set(false);
    this.editingFamily.set(null);
  }

  onFamilyCreated(): void {
    this.showCreateFamilyModal.set(false);
    this.editingFamily.set(null);
    this.loadPageData();
  }

  // ── Remove Member ────────────────────────────────────────────────────────

  confirmRemoveMember(member: FamilyMemberItem): void {
    this.openMenuId.set(null);
    this.memberToRemove.set(member);
  }

  cancelRemove(): void {
    this.memberToRemove.set(null);
  }

  async removeMember(): Promise<void> {
    const member = this.memberToRemove();
    if (!member) return;

    const resp = await this.api.request(
      'POST',
      API_ROUTES.REMOVE_FAMILY_MEMBER,
      { familyMemberId: member.familyMemberId, familySpaceId: this.activeFamilySpaceId() ?? '' },
      { showToaster: true, showLoader: false, useToken: true },
    );

    if (resp.status) {
      this.memberToRemove.set(null);
      const id = this.activeFamilySpaceId();
      if (id) this.loadFamilyMembers(id);
      this.loadPageData();
    }
  }

  // ── Three-dots menu ──────────────────────────────────────────────────────

  toggleMenu(memberId: string): void {
    this.openMenuId.set(this.openMenuId() === memberId ? null : memberId);
  }

  /**
   * Returns the CSS class string for an invite status badge.
   */
  getInviteStatusClass(status: string): string {
    return status?.toLowerCase() ?? '';
  }

  /** @deprecated backend returns correct fullName — use member.fullName directly. */
  getMemberDisplayName(member: FamilyMemberItem): string {
    return member.fullName || '—';
  }

  /** @deprecated backend returns correct levelName — use group.levelName directly. */
  getGroupTitle(group: FamilyTreeGroup): string {
    return group.levelName;
  }

  onTreePhotoError(memberId: string): void {
    this._failedTreePhotoIds.update(s => new Set([...s, memberId]));
  }

  treePhotoFailed(memberId: string): boolean {
    return this._failedTreePhotoIds().has(memberId);
  }

  onGridPhotoError(memberId: string): void {
    this._failedGridPhotoIds.update(s => new Set([...s, memberId]));
  }

  gridPhotoFailed(memberId: string): boolean {
    return this._failedGridPhotoIds().has(memberId);
  }

  /**
   * Returns up to 2 uppercase initials from a display name.
   * e.g. "John Doe" → "JD", "Alice" → "A"
   */
  getInitials(name: string | undefined): string {
    if (!name?.trim()) return '?';
    return name.trim()
      .split(/\s+/)
      .slice(0, 2)
      .map(w => w[0].toUpperCase())
      .join('');
  }

  formatPhone(value: string | undefined): string {
    if (!value) return '';
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  private scrollToPanel(selector: string): void {
    setTimeout(() => {
      const el = document.querySelector(selector);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Prefer Kendo dropdown wrapper (tabindex="0") over native inputs so the
      // family-group dropdown receives focus first when in add mode.
      const firstFocusable = el.querySelector<HTMLElement>(
        '.k-dropdownlist[tabindex], input:not([type="hidden"]), select, textarea',
      );
      firstFocusable?.focus();
    });
  }
}

